import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { ApiError } from '@/lib/apiClient'
import { cn } from '@/lib/cn'

import type { WithId } from '../useCollection'

/**
 * Une ligne de liste, telle que l'écran veut la résumer.
 *
 * Le résumé est délibérément court : dans une liste de quarante plats, ce
 * qu'on cherche c'est un nom et un prix. Le reste s'ouvre.
 */
export interface RowSummary {
  title: string
  subtitle?: string
  /** Affiché à droite, en chiffres tabulaires : prix, capacité, quantité. */
  value?: string
  badges?: { label: string; tone?: 'neutral' | 'warning' | 'success' | 'danger' }[]
  /** Grisé sans être caché : un plat retiré de la vente reste consultable. */
  dimmed?: boolean
}

/**
 * Liste + tiroir d'édition, partagés par toutes les collections.
 *
 * Le tiroir plutôt qu'une page dédiée : on modifie un prix en gardant la
 * liste sous les yeux, et on enchaîne dix plats sans dix allers-retours.
 *
 * La suppression demande confirmation **dans la ligne**, pas dans une fenêtre
 * modale : une boîte de dialogue qu'on valide par réflexe ne protège de rien.
 *
 * ─── L'ordre ───
 *
 * Quand `onReorder` est fourni, chaque ligne reçoit deux flèches. Deux boutons
 * plutôt qu'un glisser-déposer : la liste vit dans une page qui défile, et
 * traîner un élément sur trois écrans de haut se termine invariablement par un
 * lâcher au mauvais endroit. Les flèches marchent aussi au doigt et au clavier.
 *
 * L'appel envoie la liste **complète** dans le nouvel ordre, jamais « déplace
 * celui-ci d'un cran » : deux personnes qui rangent la même liste en même temps
 * produiraient sinon des positions en double, et un affichage qui change à
 * chaque rechargement.
 */
export function CollectionEditor<T extends WithId>({
  title,
  description,
  items,
  summarize,
  renderForm,
  onCreate,
  onDelete,
  onReorder,
  busy,
  error,
  emptyLabel,
  createLabel,
  canDelete,
}: {
  title: string
  description?: string
  items: T[]
  /** Le rang sert aux collections où la position a un sens visible. */
  summarize: (item: T, index: number) => RowSummary
  /** Le formulaire du tiroir. `null` pour une création. */
  renderForm: (item: T | null, close: () => void) => ReactNode
  onCreate?: () => void
  onDelete?: (item: T) => void
  /** Reçoit la liste entière dans le nouvel ordre. Absent = pas de flèches. */
  onReorder?: (ids: string[]) => void
  busy: boolean
  error: unknown
  emptyLabel: string
  /** `null` : la collection ne se crée pas depuis ici, aucun bouton n'est
      affiché. Un bouton qui n'agit pas est pire que pas de bouton. */
  createLabel: string | null
  canDelete?: (item: T) => boolean
}) {
  const { t } = useTranslation()
  const [openId, setOpenId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [confirming, setConfirming] = useState<string | null>(null)

  /** Échange deux voisins et renvoie l'ordre complet. */
  const move = (index: number, delta: number) => {
    const target = index + delta
    if (!onReorder || target < 0 || target >= items.length) return
    const ids = items.map((row) => row.id)
    ;[ids[index], ids[target]] = [ids[target]!, ids[index]!]
    onReorder(ids)
  }

  // Le tiroir se referme si la ligne ouverte disparaît — après une
  // suppression, par exemple. Sans cela il resterait ouvert sur un objet
  // qui n'existe plus.
  useEffect(() => {
    if (openId && !items.some((item) => item.id === openId)) setOpenId(null)
  }, [items, openId])

  return (
    // `min-w-0` : dans une grille, un titre tronqué imposerait sinon sa
    // largeur entière, et la page déborderait sur téléphone.
    <section className="min-w-0 rounded-lg border border-line-subtle bg-bg-elevated">
      <header className="flex flex-wrap items-center justify-between gap-4 border-b border-line-subtle px-4 py-4 sm:px-6">
        <div className="min-w-0">
          <h2 className="font-display text-heading-md text-content-primary">{title}</h2>
          {description ? (
            <p className="mt-1 text-sm text-content-secondary">{description}</p>
          ) : null}
        </div>
        {createLabel === null ? null : (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => {
              setOpenId(null)
              setCreating(true)
              onCreate?.()
            }}
          >
            {createLabel}
          </Button>
        )}
      </header>

      {error instanceof ApiError ? (
        <p className="border-b border-line-subtle px-4 py-3 text-sm text-state-danger sm:px-6">
          {error.message}
        </p>
      ) : null}

      {creating ? (
        <div className="border-b border-line-subtle bg-bg-base/40 px-4 py-5 sm:px-6">
          {renderForm(null, () => setCreating(false))}
        </div>
      ) : null}

      {items.length === 0 && !creating ? (
        <p className="px-4 py-8 text-sm text-content-muted sm:px-6">{emptyLabel}</p>
      ) : (
        <ul className="divide-y divide-line-subtle">
          {items.map((item, index) => {
            const summary = summarize(item, index)
            const isOpen = item.id === openId

            return (
              <li key={item.id}>
                <div
                  className={cn(
                    'flex items-center gap-2 px-4 py-3.5 transition duration-[var(--dur-fast)] sm:gap-4 sm:px-6',
                    isOpen ? 'bg-bg-base/40' : 'hover:bg-bg-overlay/40',
                    summary.dimmed && 'opacity-55',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setCreating(false)
                      setOpenId(isOpen ? null : item.id)
                    }}
                    aria-expanded={isOpen}
                    className="flex min-h-10 min-w-0 flex-1 flex-col justify-center gap-1.5 text-left sm:flex-row sm:items-center sm:justify-start sm:gap-4"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-content-primary">
                        {summary.title}
                      </span>
                      {summary.subtitle ? (
                        <span className="block truncate text-xs text-content-muted">
                          {summary.subtitle}
                        </span>
                      ) : null}
                    </span>

                    {/* Pastilles et valeur voyagent ensemble : sur un
                        téléphone elles passent sous le nom, sur un écran large
                        elles reprennent leur place à droite. Elles ne sont plus
                        masquées en dessous de `sm` — « Désactivé » est
                        justement ce qu'on vient vérifier d'un pouce. */}
                    {summary.badges?.length || summary.value ? (
                      <span className="flex flex-wrap items-center gap-x-3 gap-y-1 sm:justify-end">
                        {summary.badges?.map((badge) => (
                          <span
                            key={badge.label}
                            className={cn(
                              'shrink-0 rounded-full border px-2 py-0.5 text-[10px] uppercase',
                              badge.tone === 'warning' &&
                                'border-state-warning/40 text-state-warning',
                              badge.tone === 'success' &&
                                'border-state-success/40 text-state-success',
                              badge.tone === 'danger' &&
                                'border-state-danger/40 text-state-danger',
                              (!badge.tone || badge.tone === 'neutral') &&
                                'border-line text-content-muted',
                            )}
                          >
                            {badge.label}
                          </span>
                        ))}

                        {/* `break-words` et non `shrink-0` : sur la page
                            Équipe cette valeur est la liste des rôles, et un
                            texte long qui refuse de rétrécir pousse le bouton
                            de suppression hors de l'écran. */}
                        {summary.value ? (
                          <span className="min-w-0 break-words text-sm tabular-nums text-champagne-300">
                            {summary.value}
                          </span>
                        ) : null}
                      </span>
                    ) : null}
                  </button>

                  {/* Les flèches d'abord : on range plus souvent qu'on ne
                      supprime, et la suppression garde sa place à l'extrême
                      droite, loin du geste courant. */}
                  {onReorder ? (
                    <span className="flex shrink-0 gap-0.5">
                      <button
                        type="button"
                        aria-label={t('admin.moveUp', { name: summary.title })}
                        disabled={busy || index === 0}
                        onClick={() => move(index, -1)}
                        className="h-10 w-9 rounded-md text-content-muted transition hover:text-content-primary disabled:opacity-30 sm:h-8 sm:w-8"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        aria-label={t('admin.moveDown', { name: summary.title })}
                        disabled={busy || index === items.length - 1}
                        onClick={() => move(index, 1)}
                        className="h-10 w-9 rounded-md text-content-muted transition hover:text-content-primary disabled:opacity-30 sm:h-8 sm:w-8"
                      >
                        ↓
                      </button>
                    </span>
                  ) : null}

                  {onDelete && (canDelete?.(item) ?? true) ? (
                    confirming === item.id ? (
                      <span className="flex shrink-0 items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={busy}
                          onClick={() => setConfirming(null)}
                        >
                          {t('admin.cancel')}
                        </Button>
                        <Button
                          size="sm"
                          disabled={busy}
                          onClick={() => {
                            setConfirming(null)
                            onDelete(item)
                          }}
                        >
                          {t('admin.confirmDelete')}
                        </Button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirming(item.id)}
                        aria-label={t('admin.delete')}
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-content-muted transition hover:text-state-danger"
                      >
                        ✕
                      </button>
                    )
                  ) : null}
                </div>

                {isOpen ? (
                  <div className="border-t border-line-subtle bg-bg-base/40 px-4 py-5 sm:px-6">
                    {renderForm(item, () => setOpenId(null))}
                  </div>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

/** Pied de formulaire : enregistrer, ou renoncer. */
export function FormActions({
  busy,
  onSubmit,
  onCancel,
  submitLabel,
}: {
  busy: boolean
  onSubmit: () => void
  onCancel: () => void
  submitLabel?: string
}) {
  const { t } = useTranslation()

  return (
    <div className="mt-5 flex flex-wrap gap-2">
      <Button size="sm" disabled={busy} onClick={onSubmit}>
        {busy ? t('state.loading') : (submitLabel ?? t('admin.save'))}
      </Button>
      <Button variant="ghost" size="sm" disabled={busy} onClick={onCancel}>
        {t('admin.cancel')}
      </Button>
    </div>
  )
}
